import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import {
  formatAdminAccountIdentityLabel,
  formatAdminLinkedProviderCountLabel,
  formatAdminProviderIdentityLabel,
} from "../../shared/formatting/format-admin-account-identity";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import {
  formatAdminTelemetryRuntimeCoverageLabel,
  formatAdminTelemetryRuntimeSummaryLabel,
} from "../../shared/formatting/format-admin-runtime-context";
import { formatAdminAccountDeletionStatusLabel } from "../../shared/locale/admin-dashboard-status-copy";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import {
  ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminProjectionFreshness,
  type AdminUserSummaryQuery,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { useAdminAutoRefreshTick } from "../../shared/query/admin-auto-refresh";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import {
  buildFailureDrilldownTo,
  buildRecentFailureContextLabels,
} from "./user-data-view-model";
import { searchUserSummaries } from "./users-use-cases";

export type UserSummaryListItemViewModel = {
  internalUserId: string;
  userIdHash: string;
  accountIdentityLabel: string;
  providerIdentityLabel: string;
  linkedProviderCountLabel: string;
  latestDeviceInstallIdLabel: string;
  latestPlatformLabel: string;
  latestRuntimeSummaryLabel: string;
  latestRuntimeCoverageLabel: string | null;
  accountDeletionStatusLabel: string;
  detailRoute: string;
  latestReleaseLabel: string;
  countsLabel: string;
  riskLabel: string;
  topRiskLabel?: string;
  latestFailureLabel?: string;
  latestFailureDrilldownTo?: string;
  latestFailureContextLabels: string[];
  latestSuccessLabel?: string;
  latestActivityAtLabel: string;
};

export type UserSummaryListViewModel = {
  requestId: string;
  generatedAtLabel: string;
  items: UserSummaryListItemViewModel[];
  totalCountLabel: string;
};

export type UserSummaryListScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminUserSummaryQuery;
};

export type UserSummaryListScreenModel = AdminScreenState<UserSummaryListViewModel>;

function createDefaultUserSummaryQuery(): AdminUserSummaryQuery {
  return {
    timeRange: {
      from: ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
      to: new Date().toISOString(),
    },
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  };
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);
  if (values.includes("stale")) return "stale";
  if (values.includes("partial") || values.includes("unavailable")) return "partial";
  return "ready";
}

function formatCountsLabel(item: {
  counts: { pets: number; records: number; receipts: number; files: number };
}, locale: AdminDashboardLocale): string {
  return locale === "ko"
    ? `펫 ${item.counts.pets} · 기록 ${item.counts.records} · 영수증 ${item.counts.receipts} · 파일 ${item.counts.files}`
    : `Pets ${item.counts.pets} · Records ${item.counts.records} · Receipts ${item.counts.receipts} · Files ${item.counts.files}`;
}

function formatRiskLabel(item: { riskSummary: { highestLevel: string; items: Array<unknown> } }, locale: AdminDashboardLocale): string {
  const count = item.riskSummary.items.length;
  if (item.riskSummary.highestLevel === "none") {
    return locale === "ko" ? "위험 신호 없음" : "No risk signals";
  }
  return locale === "ko"
    ? `${item.riskSummary.highestLevel} / ${count}개 신호`
    : `${item.riskSummary.highestLevel} / ${count} signals`;
}

function pickLatestActivityAt(item: {
  latestFailure?: { occurredAt: string };
  latestSuccessfulSave?: { occurredAt: string };
  lastLoginAt?: string;
  latestRuntimeContextAt?: string;
  createdAt: string;
}): string {
  const candidateTimestamps = [
    item.latestFailure?.occurredAt,
    item.latestSuccessfulSave?.occurredAt,
    item.lastLoginAt,
    item.latestRuntimeContextAt,
    item.createdAt,
  ].filter((value): value is string => typeof value === "string" && value.length > 0);

  return candidateTimestamps.sort((left, right) => right.localeCompare(left))[0] ?? item.createdAt;
}

function toViewModel(
  envelope: Awaited<ReturnType<typeof searchUserSummaries>>,
  locale: AdminDashboardLocale,
): UserSummaryListViewModel {
  return {
    requestId: envelope.meta.requestId,
    generatedAtLabel: formatAdminDate(envelope.meta.generatedAt),
    totalCountLabel: locale === "ko" ? `${envelope.data.items.length}명 표시` : `${envelope.data.items.length} users shown`,
    items: envelope.data.items.map((item) => ({
      internalUserId: item.internalUserId,
      userIdHash: item.userIdHash,
      accountIdentityLabel: formatAdminAccountIdentityLabel(item.accountIdentity, locale),
      providerIdentityLabel: formatAdminProviderIdentityLabel(item.accountIdentity, locale),
      linkedProviderCountLabel: formatAdminLinkedProviderCountLabel(item.accountIdentity, locale),
      latestDeviceInstallIdLabel: item.latestDeviceInstallId ?? (locale === "ko" ? "기기 정보 없음" : "No device install"),
      latestPlatformLabel: item.latestPlatform ?? (locale === "ko" ? "알 수 없음" : "unknown"),
      latestRuntimeSummaryLabel: formatAdminTelemetryRuntimeSummaryLabel(
        {
          appBuildNumber: item.latestRuntimeAppBuildNumber,
          deviceModelNameBucket: item.latestRuntimeDeviceModelNameBucket,
          osVersionMinorBucket: item.latestRuntimeOsVersionMinorBucket,
          networkTypeBucket: item.latestRuntimeNetworkTypeBucket,
          physicalDeviceBucket: item.latestRuntimePhysicalDeviceBucket,
        },
        locale,
      ),
      latestRuntimeCoverageLabel: formatAdminTelemetryRuntimeCoverageLabel(
        {
          deviceInstallId: item.latestDeviceInstallId ?? null,
          platform: item.latestPlatform ?? null,
          appBuildNumber: item.latestRuntimeAppBuildNumber,
          deviceModelNameBucket: item.latestRuntimeDeviceModelNameBucket,
          osVersionMinorBucket: item.latestRuntimeOsVersionMinorBucket,
          networkTypeBucket: item.latestRuntimeNetworkTypeBucket,
          physicalDeviceBucket: item.latestRuntimePhysicalDeviceBucket,
        },
        locale,
      ),
      accountDeletionStatusLabel: formatAdminAccountDeletionStatusLabel(
        item.accountDeletionStatus ?? "none",
        locale,
      ),
      detailRoute: `/admin/users/${item.internalUserId}`,
      latestReleaseLabel: item.latestRelease ?? item.latestAppVersion ?? (locale === "ko" ? "릴리스 정보 없음" : "No release"),
      countsLabel: formatCountsLabel(item, locale),
      riskLabel: formatRiskLabel(item, locale),
      topRiskLabel: item.riskSummary.items[0]?.label,
      latestFailureLabel: item.latestFailure
        ? `${item.latestFailure.errorCode} · ${item.latestFailure.feature}`
        : undefined,
      latestFailureDrilldownTo: item.latestFailure
        ? buildFailureDrilldownTo(item.latestFailure)
        : undefined,
      latestFailureContextLabels: item.latestFailure
        ? buildRecentFailureContextLabels(item.latestFailure, locale)
        : [],
      latestSuccessLabel: item.latestSuccessfulSave
        ? `${item.latestSuccessfulSave.event}`
        : undefined,
      latestActivityAtLabel: formatAdminDate(pickLatestActivityAt(item)),
    })),
  };
}

function createDependenciesKey(dependencies: UserSummaryListScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    query: dependencies.query ?? null,
  });
}

export async function resolveUserSummaryListScreenModel(
  dependencies: UserSummaryListScreenModelDependencies,
): Promise<UserSummaryListScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.users.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "User summary list read permission denied.",
        ko: "사용자 목록 조회 권한이 없습니다.",
      },
      "admin_user_summary_list_permission_denied",
    );
  }

  try {
    const envelope = await searchUserSummaries(
      dependencies.queryPort,
      dependencies.query ?? createDefaultUserSummaryQuery(),
    );
    if (envelope.data.items.length === 0) {
      return { status: "empty" };
    }

    const viewModel = toViewModel(envelope, dependencies.locale ?? "ko");
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);
    if (freshnessPriority === "stale") return { status: "stale", viewModel };
    if (freshnessPriority === "partial") return { status: "partial", viewModel };
    return { status: "ready", viewModel };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useUserSummaryListScreenModel(
  dependencies: UserSummaryListScreenModelDependencies,
): UserSummaryListScreenModel {
  const [state, setState] = useState<UserSummaryListScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);
  const refreshTick = useAdminAutoRefreshTick(dependencies.runtimeConfig);

  useEffect(() => {
    let cancelled = false;
    setState((current) => createLoadingAdminScreenState(current.viewModel));
    void resolveUserSummaryListScreenModel(dependencies).then((nextState) => {
      if (!cancelled) setState(nextState);
    });
    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey, refreshTick]);

  return state;
}
